<template>
  <div class="table-responsive">
    <a-table
      :columns="columns"
      :data-source="dataSource"
      :bordered="true"
      :row-key="(rec, idx) => idx"
      :scroll="{ x: true }"
      :pagination="false"
      size="small"
      :loading="tableLoading"
    >
      <template #expandedRowRender="{record}">
        <div class="p-3 mb-3">
          <div><h5>Rate Card</h5></div>
          <div>
            <rate-card-table :zone-rate="record?.zoneRate" />
          </div>
        </div>
      </template>
      <template #docType="{text}">
        <a-tag color="blue">
          <span class="align-middle font-size-14 text-uppercase">
            {{ text || 'N/A' }}
          </span>
        </a-tag>
      </template>
      <template #actions="{record}">
        <div>
          <ButtonGroupActions
            :button-list="actionButtonList"
            @handleEditClick="handleEditClick(record)"
            @handleDeleteClick="handleDeleteClick(record)"
          />
        </div>
      </template>
    </a-table>
  </div>
  <div>
    <ModalSlot v-model:modal-visible="isRateModalVisible" modal-title="Add Rate" modal-width="800">
      <div style="height: 430px; width: 100%; overflow-y: auto; overflow-x: hidden">
        <RatesForm :edit-data="editFormData" />
      </div>
    </ModalSlot>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, reactive, ref } from 'vue'
import RatesForm from '../rates/new/index.vue'
import { useSearchMapping } from '@/hooks/useSearchMappingHook'
import { IZoneRate } from '@/types/interfaces/IZoneRate'
import RateCardTable from './RateCardTable.vue'
import { ShippingRatesDocument } from '@/types/firebaseCollectionContracts/ShippingRatesDocument'
import { RateFormData } from '@/types/appcontracts/RateFormData'
import { RateTypes } from '@/types/enums/RateTypes'
import ModalSlot from '@/components/General/ModalSlot.vue'
import { DocumentOperation } from '@/types/appcontracts/DocumentOperation'
import { DocumentActions } from '@/types/enums/DocumentActions'
import * as actions from '@/store/actions.type'
import { useStore } from 'vuex'
import ButtonGroupActions from '@/components/General/ButtonGroupActions.vue'
import { ActionButtonList } from '@/types/enums/ActionButtonList'

interface IZoneRateTable {
  zoneRate: IZoneRate
  zoneName: string
  vendorName: string
  serviceName: string
  vendorId: string
}

const columns = [
  {
    title: 'Zone Name',
    dataIndex: 'zoneName',
    key: 'zoneName',
  },
  {
    title: 'Document Type',
    dataIndex: 'zoneRate.documentType',
    key: 'docType',
    slots: { customRender: 'docType' },
  },
  {
    title: 'Vendor',
    dataIndex: 'vendorName',
    key: 'vendorName',
  },
  {
    title: 'Service',
    dataIndex: 'serviceName',
    key: 'serviceName',
  },
  {
    title: 'Actions',
    key: 'actions',
    slots: { customRender: 'actions' },
  },
]

export default defineComponent({
  name: 'ZoneRateTable',
  components: { RateCardTable, RatesForm, ModalSlot, ButtonGroupActions },
  props: {
    shippingRatesDoc: {
      type: Object as PropType<ShippingRatesDocument | null>,
      required: true,
    },
  },
  setup(props) {
    const store = useStore()
    // const zoneRatesDataSource = computed(() => props.zoneRatesList)
    const { getZoneDetailsById } = useSearchMapping()
    const isRateModalVisible = ref(false)
    const editFormData = ref<RateFormData | null>(null)
    const tableLoading = ref<boolean>(false)
    const actionButtonList = [ActionButtonList.EDIT, ActionButtonList.DELETE]
    const dataSource = computed(() => {
      return props.shippingRatesDoc?.zoneRates?.map((x) => {
        const zoneDetails = getZoneDetailsById(x.zoneId || '')
        return {
          zoneRate: x,
          zoneName: zoneDetails.name,
          vendorName: zoneDetails.vendorName,
          serviceName: zoneDetails.serviceName,
          vendorId: zoneDetails.vendorId,
        } as IZoneRateTable
      })
    })
    const totalTableRecords = computed(() => dataSource.value?.length) // Currently no way to know total Rates doc
    const showTotal = (total: number, range: Array<any>) => {
      return `${range[0]}-${range[1]} of ${total} items`
    }

    const handleEditClick = (record: IZoneRateTable) => {
      editFormData.value = {
        ...record.zoneRate,
        customerId: props.shippingRatesDoc?.customerId || null,
        vendorId: record.vendorId || null,
        rateType: props.shippingRatesDoc?.rateType || RateTypes.Customer,
      }

      isRateModalVisible.value = true
    }

    const handleDeleteClick = async (record: IZoneRateTable) => {
      const del: RateFormData = {
        ...record.zoneRate,
        customerId: props.shippingRatesDoc?.customerId || null,
        vendorId: record.vendorId || null,
        rateType: props.shippingRatesDoc?.rateType || RateTypes.Customer,
      }
      tableLoading.value = true
      const docOperation: DocumentOperation<RateFormData> = {
        action: DocumentActions.DELETE,
        id: del.customerId || del.vendorId,
        payload: del,
        audit: null,
      }
      await store.dispatch(`rates/${actions.RatesAction.SET_OR_DELETE_SHIPPING_RATE}`, docOperation)
      tableLoading.value = false
    }

    const pagination = reactive({
      position: 'bottom',
      showSizeChanger: true,
      showQuickJumper: false,
      total: totalTableRecords.value || 0,
      simple: false,
      pageSize: 10,
      showTotal: showTotal,
    })

    return {
      columns,
      pagination,
      dataSource,
      isRateModalVisible,
      editFormData,
      handleEditClick,
      tableLoading,
      actionButtonList,
      handleDeleteClick,
    }
  },
})
</script>

<style lang="scss">
.ant-pagination-item,
.ant-pagination-jump-next,
.ant-pagination-jump-prev {
  display: none !important;
}
tr.ant-table-expanded-row,
tr.ant-table-expanded-row:hover {
  background: #f0f2f40d !important;
}
</style>
